import React, { useEffect } from 'react';

import { APP } from './captivision.config';
import { CVEnglishOnly, CVFavicon, CVTopbar, CVFooter, contentLanguage, useCaptiVisionTheme } from './shell.component';

import './captivision.styles.scss';

// The frame every CaptiVision document page shares - get started, privacy and
// terms: the page title, the header block with its eyebrow and lede, and the
// article underneath. The full guide adds a contents rail, so it builds its
// own frame from the same shell pieces. `lang` is the language the content is
// in (see useLocalizedPage); until it is known, only the chrome is drawn.

// a numbered section heading, "03  Where your information goes"
export const CVHeading = ({ id, num, children }) => (
    <h2 id={id}>
        <span className="cv-num"> {num} </span>
        {children}
    </h2>
)

// Numbers a list of { id, title, body } sections in order, so a section that
// is switched off never leaves a gap in the numbering.
export const CVSections = ({ sections }) => (
    sections.map((section, index) => (
        <React.Fragment key={section.id}>
            <CVHeading id={section.id} num={String(index + 1).padStart(2, '0')}> {section.title} </CVHeading>
            {section.body}
        </React.Fragment>
    ))
)

export const useDocumentTitle = title => {
    useEffect(() => {
        document.title = title ? `${title} | ${APP.name}` : APP.name

        return () => { document.title = APP.name }
    }, [title])
}

// `note` is a line under the header, such as a translated legal page saying
// that the English text is the one that applies
const CVDocument = ({ title, eyebrow, lede, meta, note, subnav, navCurrent, footerCurrent, lang, children }) => {
    const [theme, toggleTheme] = useCaptiVisionTheme();

    useDocumentTitle(title)

    // to the top when the page opens, but not when its language changes
    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    return (
        <div className="captivision cv-document">
            <CVFavicon />
            <CVTopbar theme={theme} onToggleTheme={toggleTheme} current={navCurrent} />
            <CVEnglishOnly lang={lang} />

            {lang ? (
                <main className="cv-doc" {...contentLanguage(lang)}>
                    <header className="cv-dochead">
                        <span className="cv-eyebrow"> {eyebrow} </span>
                        <h1> {title} </h1>
                        <p className="cv-lede"> {lede} </p>
                        {meta ? <p className="cv-meta"> {meta} </p> : null}
                        {note ? <p className="cv-doc-note"> {note} </p> : null}
                        {subnav}
                    </header>

                    <article className="cv-prose">
                        {children}
                    </article>
                </main>
            ) : <main className="cv-doc" />}

            <CVFooter current={footerCurrent} />
        </div>
    )
}

export default CVDocument;
